import { supabase } from '@/integrations/supabase/client';
import type { Tables } from '@sylva/shared-types/database';
import { z } from 'zod';
import { levenshteinDistance } from '@/lib/stringUtils';
import { normalizePhoneToE164 } from '@sylva/shared-types';

// Use customer_status view which includes status and cohort columns
// Note: Types are auto-generated but we extend here for safety until codegen runs
export type CustomerRow = Tables<'customer_status'> & {
  status?: 'customer' | 'lead' | 'imported' | null;
  created_last_7d?: boolean | null;
  created_last_14d_7d?: boolean | null;
};
// Base customer type without computed fields (for export)
export type CustomerBaseRow = Tables<'customers'>;
export type AppointmentRow = Pick<Tables<'appointments'>, 'customer_id'>;

// Define Zod schema first
const CustomersQueryDataSchema = z.object({
  customers: z.array(z.any()), // CustomerRow type is complex, using z.any() for simplicity
  appointments: z.array(z.object({ customer_id: z.string().nullable() })),
});

// Infer type from schema
export type CustomersQueryData = z.infer<typeof CustomersQueryDataSchema>;

// PostgREST caps each response at the project's max_rows (1000). To load the
// whole customer book we page through it in chunks via .range().
const CUSTOMER_PAGE_SIZE = 1000;

export const fetchCustomersAndAppointments = async (tenantId: string, limit?: number): Promise<CustomersQueryData> => {
  // Fetch customers from the customer_status view (status + cohort columns),
  // paging in 1000-row chunks until we've loaded everything (or hit `limit`).
  const target = limit ?? Number.POSITIVE_INFINITY;
  const customers: CustomerRow[] = [];

  for (let from = 0; from < target; from += CUSTOMER_PAGE_SIZE) {
    const to = Math.min(from + CUSTOMER_PAGE_SIZE, target) - 1;
    const { data, error } = await supabase
      .from('customer_status')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false })
      .range(from, to);

    if (error) throw error;
    const batch = data || [];
    customers.push(...(batch as CustomerRow[]));
    if (batch.length < CUSTOMER_PAGE_SIZE) break; // last page reached
  }

  // Note: appointments array is kept for backward compatibility but is now deprecated
  // Use customer.status and customer.appointment_count instead
  return {
    customers,
    appointments: [], // Deprecated: use customer.status instead
  };
};

// Function to get the total customer count for a tenant
export const fetchCustomerCount = async (tenantId: string): Promise<number> => {
  // Zaehlt auf der Tabelle, nicht auf der View customer_status. Die View
  // berechnet pro Zeile zwei Unterabfragen auf appointments (Status und
  // Termin-Anzahl) -- fuer eine reine Anzahl ist das komplett verschenkt.
  // Die View hat kein WHERE, ist also 1:1 zur Tabelle; am 29.09.2026 ueber
  // alle Tenants gegengeprueft: identische Zahlen, keine Abweichung.
  const { count, error } = await supabase
    .from('customers')
    .select('id', { count: 'exact', head: true })
    .eq('tenant_id', tenantId);

  if (error) throw error;
  return count || 0;
};

// Helper function to detect if a query looks like a phone number
const looksLikePhoneNumber = (query: string): boolean => {
  const cleaned = query.replace(/\s/g, '');
  // Check if it starts with 0, +, or consists mostly of digits
  return /^[0+]/.test(cleaned) || /^\d{6,}$/.test(cleaned);
};

// Calculate similarity score for search results
const calculateSimilarity = (customer: CustomerRow, searchQuery: string): number => {
  const query = searchQuery.toLowerCase().trim();
  const words = query.split(/\s+/).filter(word => word.length > 0);
  const fullName = `${customer.first_name || ''} ${customer.last_name || ''}`.toLowerCase().trim();
  const firstName = (customer.first_name || '').toLowerCase();
  const lastName = (customer.last_name || '').toLowerCase();

  if (words.length === 2) {
    // For two-word searches, heavily prioritize exact first+last combinations
    const [word1, word2] = words;

    // Check for exact first+last match (either order)
    const exactMatch1 = firstName.includes(word1) && lastName.includes(word2);
    const exactMatch2 = firstName.includes(word2) && lastName.includes(word1);

    if (exactMatch1 || exactMatch2) {
      // For exact combinations, use very low distance (highest priority)
      return levenshteinDistance(query, fullName);
    }

    // For non-exact matches, add penalty to push them down
    const fullNameDistance = levenshteinDistance(query, fullName) + 10;
    const firstNameDistance = levenshteinDistance(query, firstName) + 15;
    const lastNameDistance = levenshteinDistance(query, lastName) + 15;

    return Math.min(fullNameDistance, firstNameDistance, lastNameDistance);
  } else {
    // For single word or 3+ words, use normal distance
    const fullNameDistance = levenshteinDistance(query, fullName);
    const firstNameDistance = levenshteinDistance(query, firstName);
    const lastNameDistance = levenshteinDistance(query, lastName);

    return Math.min(fullNameDistance, firstNameDistance, lastNameDistance);
  }
};

// Function to search customers directly in the database
export const searchCustomersInDatabase = async (
  tenantId: string,
  searchQuery: string,
  limit: number = 100
): Promise<CustomerRow[]> => {
  if (!searchQuery.trim()) {
    return [];
  }

  const query = searchQuery.toLowerCase().trim();
  const words = query.split(/\s+/).filter(word => word.length > 0);

  // Build search conditions for all words
  let conditions = words.flatMap(word =>
    [`first_name.ilike.%${word}%`, `last_name.ilike.%${word}%`, `email.ilike.%${word}%`, `phone.ilike.%${word}%`]
  );

  // If query looks like a phone number, also search for the normalized E.164 version
  if (looksLikePhoneNumber(query)) {
    const cleanQuery = query.replace(/\s/g, '');
    const normalizedPhone = normalizePhoneToE164(cleanQuery, 'de');

    if (normalizedPhone) {
      // Add exact phone match for E.164 format
      conditions.push(`phone.eq.${normalizedPhone}`);
    }

    // For partial phone numbers starting with 0 (German national format),
    // also search for the E.164 pattern (replace leading 0 with +49)
    if (cleanQuery.startsWith('0') && cleanQuery.length >= 2) {
      const e164Pattern = '+49' + cleanQuery.substring(1);
      conditions.push(`phone.ilike.${e164Pattern}%`);
    }
  }

  const conditionsString = conditions.join(',');

  // Get all potential matches (more than limit to sort properly)
  const { data: allMatches, error } = await supabase
    .from('customer_status')
    .select('*')
    .eq('tenant_id', tenantId)
    .or(conditionsString)
    .limit(limit * 2);

  if (error) throw error;

  if (!allMatches || allMatches.length === 0) {
    return [];
  }

  // Sort by similarity (distance) to search query
  const sortedMatches = allMatches.sort((a, b) => {
    const aDistance = calculateSimilarity(a as any, searchQuery);
    const bDistance = calculateSimilarity(b as any, searchQuery);

    // Lower distance = better match = should come first
    return aDistance - bDistance;
  });

  return sortedMatches.slice(0, limit) as CustomerRow[];
};

// Function to fetch ALL customers for export (no limit)
// We use the view to include status and appointment_count in exports
export const fetchAllCustomersForExport = async (tenantId: string): Promise<CustomerRow[]> => {
  const { data, error } = await supabase
    .from('customer_status')
    .select('*')
    .eq('tenant_id', tenantId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data || []) as CustomerRow[];
};

